import { useEffect } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Card, CardBody, EmptyState } from "@agentnative/ui";
import { useCurrentOrganizationId } from "../stores/currentOrg";
import { useAuthStore } from "../stores/auth";

export function DashboardRoute() {
  const organizationId = useCurrentOrganizationId();
  const user = useAuthStore((s) => s.user);
  const initialized = useAuthStore((s) => s.initialized);
  const navigate = useNavigate();

  // A logged-out visitor belongs on /login, not looking at a dashboard
  // empty state telling them to use a dev-only panel -- wait for
  // main.tsx's SessionBootstrap to resolve (initialized) before
  // deciding "logged out," so a real, logged-in user's session isn't
  // mistaken for a logged-out one during that brief check.
  useEffect(() => {
    if (initialized && !user) {
      navigate({ to: "/login" });
    }
  }, [initialized, user, navigate]);

  if (!initialized || !user) {
    return null;
  }

  if (!organizationId) {
    return (
      <EmptyState
        title="No organization selected"
        // A real, logged-in user with no organization membership --
        // registration auto-creates one (see console-server's
        // handleRegister), so this normally only happens for an
        // account created before that existed, or one removed from its
        // last org.
        description="Your account isn't a member of any organization yet. Ask an existing member to invite you, or use the dev auth panel above for local testing."
      />
    );
  }

  const links = [
    { to: "/settings/organization", title: "Organization", description: "Profile and settings" },
    { to: "/settings/members", title: "Members", description: "Invite and manage members" },
    { to: "/settings/billing", title: "Billing", description: "Subscription and credits" },
    { to: "/settings/licenses", title: "My Licenses", description: "Apps licensed to this org" },
    { to: "/settings/tokens", title: "API Tokens", description: "Personal tokens for scripts and the API" },
    { to: "/marketplace", title: "Marketplace", description: "Browse the app catalog" },
  ] as const;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-[var(--color-neutral-600)]">
          Acting as organization <span className="font-mono">{organizationId}</span>
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((link) => (
          <Link key={link.to} to={link.to}>
            <Card hoverable padding="md">
              <CardBody>
                <h2 className="font-medium">{link.title}</h2>
                <p className="text-sm text-[var(--color-neutral-600)]">{link.description}</p>
              </CardBody>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
